import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faClock } from '@fortawesome/free-solid-svg-icons';
import GalleryCountdown from '../shared/GalleryCountdown';
import { useExpiryCountdown } from '../../hooks/useExpiryCountdown';

interface Props {
  results: SketcherRoundResult[];
  roundNumber: number;
  totalRounds: number;
  onNext?: () => void;
  expiryTime?: string | null;
}

const SketcherGalleryScreen: React.FC<Props> = ({
  results,
  roundNumber,
  totalRounds,
  onNext,
  expiryTime,
}) => {
  const secondsLeft = useExpiryCountdown(expiryTime);

  // The display window has ended once an expiry was provided and it elapsed.
  const displayEnded = !!expiryTime && secondsLeft <= 0;

  // Highest score = lowest countDown. Intent: show the highest score LAST,
  // so sort by countDown DESCENDING (e.g. 4, 3, 2, 1) — the winner ends up
  // at the bottom of the gallery.
  const sortedResults = [...results].sort((a, b) => b.countDown - a.countDown);

  // Current countdown is the lowest countDown value in the batch (latest revealed)
  const currentCountDown =
    results.length > 0 ? Math.min(...results.map((r) => r.countDown)) : null;

  // With replace-per-event, `results` only holds the latest event. The winner
  // (countDown 1) normally arrives in the final event, but to avoid the Next
  // button flickering if events ever arrive out of order, LATCH the "all
  // revealed" signal once a countDown <= 1 has been seen this round. The latch
  // resets when the round changes.
  const [revealedLatched, setRevealedLatched] = useState(false);

  useEffect(() => {
    setRevealedLatched(false);
  }, [roundNumber]);

  useEffect(() => {
    if (currentCountDown !== null && currentCountDown <= 1) {
      setRevealedLatched(true);
    }
  }, [currentCountDown]);

  // Show Next once the winner has been revealed (latched) OR the display
  // window has ended. displayEnded is the robust fallback driven by the
  // backend's currentExpiryTime, independent of countDown values.
  const showNext =
    !!onNext && results.length > 0 && (revealedLatched || displayEnded);

  return (
    <div className="sketcher-gallery">
      {/* Header */}
      <div className="sketcher-gallery__header">
        <h2 className="sketcher-gallery__title">🖼️ Art Gallery</h2>
        <div className="sketcher-gallery__header-right">
          <GalleryCountdown expiryTime={expiryTime} label="Next round in" />
          <span className="sketcher-gallery__round">
            Round {roundNumber} of {totalRounds}
          </span>
        </div>
      </div>

      {/* Countdown display */}
      {currentCountDown !== null && (
        <div className="sketcher-gallery__countdown">
          <span className="sketcher-gallery__countdown-number">
            {currentCountDown}
          </span>
          <span className="sketcher-gallery__countdown-label">
            {currentCountDown <= 1
              ? '🏆 Best Drawing!'
              : `#${currentCountDown} Revealed`}
          </span>
        </div>
      )}

      {/* Gallery grid */}
      <div className="sketcher-gallery__grid">
        {sortedResults.map((result) => {
          // Rank is derived from countDown (1 = winner), NOT the array index,
          // so the medal stays correct even though the winner is shown last.
          const rank = result.countDown;
          const medal =
            rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
          return (
            <div key={result.playerId} className="sketcher-gallery__card">
              {/* Rank badge */}
              <div
                className={`sketcher-gallery__rank sketcher-gallery__rank--${rank}`}
              >
                {medal}
              </div>

              {/* Countdown badge on card */}
              <div className="sketcher-gallery__card-countdown">
                {result.countDown}
              </div>

              {/* Image */}
              <div className="sketcher-gallery__image-wrap">
                <img
                  src={result.imageUrl}
                  alt={`Sketch by ${result.playerName}`}
                  className="sketcher-gallery__image"
                />
              </div>

              {/* Info */}
              <div className="sketcher-gallery__info">
                <span className="sketcher-gallery__player-name">
                  {result.playerName}
                </span>
                <span className="sketcher-gallery__ranking-msg">
                  {result.rankingMessage}
                </span>

                <div className="sketcher-gallery__stats">
                  <span className="sketcher-gallery__score">
                    <FontAwesomeIcon icon={faStar} /> {result.score} pts
                  </span>
                  <span className="sketcher-gallery__time">
                    <FontAwesomeIcon icon={faClock} /> {result.timeTaken}s
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Loading indicator for incoming results */}
      {results.length === 0 && (
        <div className="sketcher-gallery__loading">
          <p>Waiting for results...</p>
        </div>
      )}

      {/* Next button once all results are revealed (lowest countDown has
          reached its floor — 0 or 1 depending on backend) or the display
          window has elapsed. */}
      {showNext && (
        <div className="sketcher-gallery__next-wrap">
          <button className="sketcher-gallery__next-btn" onClick={onNext}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SketcherGalleryScreen;
